import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import { Plus, TrendingUp, TrendingDown, DollarSign, Receipt, FileText, Printer, Download, Share2, Edit2, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ReceiptPrint from "@/components/ReceiptPrint";
import { printReceipt, downloadReceiptPDF, shareReceiptPDF, numberToWords } from "@/utils/printUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSettings } from "@/context/SettingsContext";

const getDateRange = (preset) => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0-11

  let start, end;

  switch (preset) {
    case 'today':
      start = new Date(year, month, today.getDate());
      end = new Date(year, month, today.getDate());
      break;
    case 'yesterday':
      start = new Date(year, month, today.getDate() - 1);
      end = new Date(year, month, today.getDate() - 1);
      break;
    case 'this_month':
      start = new Date(year, month, 1);
      end = new Date(year, month + 1, 0);
      break;
    case 'last_month':
      start = new Date(year, month - 1, 1);
      end = new Date(year, month, 0);
      break;
    case 'this_year':
      start = new Date(year, 0, 1);
      end = new Date(year, 11, 31);
      break;
    case 'last_year':
      start = new Date(year - 1, 0, 1);
      end = new Date(year - 1, 11, 31);
      break;
    default:
      return { start: "", end: "" };
  }

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  return { start: formatDate(start), end: formatDate(end) };
};

const FinancialLedger = () => {
  const { formatCurrency, formatDate, settings } = useSettings();
  const [searchParams] = useSearchParams();
  const receiptRef = useRef();
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingSubscriptions, setPendingSubscriptions] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isReceiptDialogOpen, setIsReceiptDialogOpen] = useState(false);
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [lastCreatedReceipt, setLastCreatedReceipt] = useState(null);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  // Filter states
  const [datePreset, setDatePreset] = useState("this_month");
  const [dateFilter, setDateFilter] = useState(() => getDateRange("this_month"));
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const handleDatePresetChange = (value) => {
    setDatePreset(value);
    if (value !== 'custom') {
      setDateFilter(getDateRange(value));
    }
  };
  
  const [receiptForm, setReceiptForm] = useState({
    receipt_type: "other_income",
    member_id: "",
    membership_id: "",
    amount: "",
    payment_method: "cash",
    description: "",
    date: new Date().toISOString().split('T')[0]
  });
  
  const [expenseForm, setExpenseForm] = useState({
    expense_type: "other",
    payee_name: "",
    amount: "",
    payment_method: "cash",
    description: "",
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
    
    // Handle URL parameters for pre-fill
    const memberId = searchParams.get('member_id');
    const membershipId = searchParams.get('membership_id');
    const amount = searchParams.get('amount');
    const type = searchParams.get('type');
    
    if (memberId || membershipId || amount || type) {
      setReceiptForm(prev => ({
        ...prev,
        receipt_type: type === 'admission_fee' || type === 'subscription_fee' || type === 'other_income'
          ? type
          : (membershipId ? "subscription_fee" : "other_income"),
        member_id: memberId || "",
        membership_id: membershipId || "",
        amount: amount || ""
      }));
      setIsReceiptDialogOpen(true);
    }
  }, [searchParams, dateFilter, categoryFilter, paymentMethodFilter, searchQuery]);

  const fetchData = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (dateFilter.start) queryParams.append('start_date', dateFilter.start);
      if (dateFilter.end) queryParams.append('end_date', dateFilter.end);
      if (categoryFilter !== 'all') queryParams.append('category', categoryFilter);
      if (paymentMethodFilter !== 'all') queryParams.append('payment_method', paymentMethodFilter);
      if (searchQuery) queryParams.append('search', searchQuery);

      const [ledgerRes, summaryRes, membersRes, pendingRes] = await Promise.all([
        api.get(`/ledger?${queryParams.toString()}`),
        api.get('/financial-summary'),
        api.get('/members'),
        api.get('/memberships/pending')
      ]);
      
      setLedger(ledgerRes.data);
      setSummary(summaryRes.data);
      setMembers(membersRes.data);
      setPendingSubscriptions(pendingRes.data);
      
      // Get company info from user
      const user = JSON.parse(localStorage.getItem('user'));
      if (user?.company) {
        setCompany(user.company);
      }
    } catch (error) {
      toast.error("Failed to load financial data");
    } finally {
      setLoading(false);
    }
  };

  const handleReceiptSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...receiptForm,
        receipt_date: receiptForm.date
      };
      const response = await api.post('/receipts', payload);
      toast.success("Receipt created successfully");
      
      // Prepare receipt for printing
      const member = members.find(m => m.id === receiptForm.member_id);
      const subscription = pendingSubscriptions.find(s => s.id === receiptForm.membership_id);
      
      const receiptData = {
        ...response.data,
        member_name: member?.name,
        member_email: member?.email,
        member_phone: member?.phone,
        plan_name: subscription?.plan?.name,
        company_name: company?.name,
        company_address: company?.address,
        company_phone: company?.phone,
        company_email: company?.email,
        company_logo_url: company?.logo_url,
        amount_in_words: numberToWords(parseFloat(receiptForm.amount))
      };
      
      setLastCreatedReceipt(receiptData);
      setShowPrintOptions(true);
      setIsReceiptDialogOpen(false);
      resetReceiptForm();
      fetchData();
      try { window.dispatchEvent(new Event('ledgerUpdated')); } catch {}
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create receipt");
    }
  };
  
  const handlePrint = (format) => {
    if (receiptRef.current) {
      printReceipt(receiptRef.current, format);
    }
  };
  
  const handleDownloadPDF = async (format) => {
    if (receiptRef.current) {
      const success = await downloadReceiptPDF(
        receiptRef.current,
        `receipt-${lastCreatedReceipt?.receipt_number}.pdf`,
        format
      );
      if (success) {
        toast.success("Receipt downloaded successfully");
      } else {
        toast.error("Failed to download receipt");
      }
    }
  };
  
  const handleSharePDF = async (format) => {
    if (receiptRef.current) {
      const success = await shareReceiptPDF(
        receiptRef.current,
        `receipt-${lastCreatedReceipt?.receipt_number}.pdf`,
        format
      );
      if (success) {
        toast.success("Receipt shared successfully");
      } else {
        toast.error("Failed to share receipt");
      }
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...expenseForm,
        expense_date: expenseForm.date
      };
      await api.post('/expenses', payload);
      toast.success("Expense recorded successfully");
      setIsExpenseDialogOpen(false);
      resetExpenseForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record expense");
    }
  };

  const resetReceiptForm = () => {
    setReceiptForm({
      receipt_type: "other_income",
      member_id: "",
      membership_id: "",
      amount: "",
      payment_method: "cash",
      description: "",
      date: new Date().toISOString().split('T')[0]
    });
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      expense_type: "other",
      payee_name: "",
      amount: "",
      payment_method: "cash",
      description: "",
      date: new Date().toISOString().split('T')[0]
    });
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setIsEditDialogOpen(true);
  };

  const handleUpdateEntry = async (e) => {
    e.preventDefault();
    try {
      const endpoint = editingEntry.type === 'receipt' ? 'receipts' : 'expenses';
      const updateData = {
        amount: parseFloat(editingEntry.amount),
        payment_method: editingEntry.payment_method,
        description: editingEntry.description,
        [editingEntry.type === 'receipt' ? 'receipt_date' : 'expense_date']: editingEntry.date
      };

      await api.patch(`/${endpoint}/${editingEntry.id}`, updateData);
      toast.success(`${editingEntry.type === 'receipt' ? 'Receipt' : 'Expense'} updated successfully`);
      setIsEditDialogOpen(false);
      setEditingEntry(null);
      fetchData();
      try { window.dispatchEvent(new Event('ledgerUpdated')); } catch {}
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to update ${editingEntry.type}`);
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(`Are you sure you want to delete this ${entry.type === 'receipt' ? 'receipt' : 'expense'}? This action cannot be undone.`)) {
      return;
    }

    try {
      const endpoint = entry.type === 'receipt' ? 'receipts' : 'expenses';
      await api.delete(`/${endpoint}/${entry.id}`);
      toast.success(`${entry.type === 'receipt' ? 'Receipt' : 'Expense'} deleted successfully`);
      fetchData();
      try { window.dispatchEvent(new Event('ledgerUpdated')); } catch {}
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${entry.type}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Financial Ledger
          </h1>
          <p className="text-zinc-500">Track all income and expenses</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Dialog open={isReceiptDialogOpen} onOpenChange={setIsReceiptDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-500 text-white hover:bg-green-600">
                <Plus size={20} className="mr-2" />
                Add Receipt
              </Button>
            </DialogTrigger>
            <DialogContent aria-describedby="receipt-dialog-description">
              <DialogHeader>
                <DialogTitle>Add Receipt (Income)</DialogTitle>
              </DialogHeader>
              <p id="receipt-dialog-description" className="sr-only">Add a new income receipt</p>
              <form onSubmit={handleReceiptSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="receipt_type">Receipt Type *</Label>
                  <Select
                    value={receiptForm.receipt_type}
                    onValueChange={(value) => setReceiptForm({ ...receiptForm, receipt_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admission_fee">Admission Fee</SelectItem>
                      <SelectItem value="subscription_fee">Subscription Fee</SelectItem>
                      <SelectItem value="other_income">Other Income</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(receiptForm.receipt_type === "admission_fee" || receiptForm.receipt_type === "subscription_fee") && (
                  <div>
                    <Label htmlFor="member_id">Member *</Label>
                    <Select
                      value={receiptForm.member_id}
                      onValueChange={(value) => setReceiptForm({ ...receiptForm, member_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select member" />
                      </SelectTrigger>
                      <SelectContent>
                        {members.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.name} ({member.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {receiptForm.receipt_type === "subscription_fee" && receiptForm.member_id && (
                  <div>
                    <Label htmlFor="membership_id">Pending Subscription *</Label>
                    <Select
                      value={receiptForm.membership_id}
                      onValueChange={(value) => setReceiptForm({ ...receiptForm, membership_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select subscription" />
                      </SelectTrigger>
                      <SelectContent>
                        {pendingSubscriptions
                          .filter(s => s.member_id === receiptForm.member_id)
                          .map((sub) => (
                            <SelectItem key={sub.id} value={sub.id}>
                              {sub.plan?.name} - Due: {formatCurrency(sub.amount_due - sub.amount_paid)}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="receipt_date">Date *</Label>
                  <Input
                    id="receipt_date"
                    type="date"
                    value={receiptForm.date}
                    onChange={(e) => setReceiptForm({ ...receiptForm, date: e.target.value })}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount">Amount *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={receiptForm.amount}
                      onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="payment_method">Payment Method *</Label>
                    <Select
                      value={receiptForm.payment_method}
                      onValueChange={(value) => setReceiptForm({ ...receiptForm, payment_method: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={receiptForm.description}
                    onChange={(e) => setReceiptForm({ ...receiptForm, description: e.target.value })}
                  />
                </div>

                <Button type="submit" className="w-full bg-green-500 hover:bg-green-600">
                  Create Receipt
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isExpenseDialogOpen} onOpenChange={setIsExpenseDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-red-500 text-white hover:bg-red-600">
                <Plus size={20} className="mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent aria-describedby="expense-dialog-description">
              <DialogHeader>
                <DialogTitle>Add Expense (Payment)</DialogTitle>
              </DialogHeader>
              <p id="expense-dialog-description" className="sr-only">Add a new expense payment</p>
              <form onSubmit={handleExpenseSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="expense_type">Expense Type *</Label>
                  <Select
                    value={expenseForm.expense_type}
                    onValueChange={(value) => setExpenseForm({ ...expenseForm, expense_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trainer_payment">Trainer Payment</SelectItem>
                      <SelectItem value="rent">Rent</SelectItem>
                      <SelectItem value="utilities">Utilities</SelectItem>
                      <SelectItem value="equipment">Equipment</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="salary">Salary</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="payee_name">Payee Name *</Label>
                  <Input
                    id="payee_name"
                    value={expenseForm.payee_name}
                    onChange={(e) => setExpenseForm({ ...expenseForm, payee_name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="expense_date">Date *</Label>
                  <Input
                    id="expense_date"
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount">Amount *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="payment_method">Payment Method *</Label>
                    <Select
                      value={expenseForm.payment_method}
                      onValueChange={(value) => setExpenseForm({ ...expenseForm, payment_method: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  />
                </div>

                <Button type="submit" className="w-full bg-red-500 hover:bg-red-600">
                  Record Expense
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Financial Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-green-700 font-medium">Total Income</p>
              <TrendingUp className="text-green-600" size={24} />
            </div>
            <p className="text-3xl font-black text-green-900">{formatCurrency(summary.total_receipts)}</p>
            <div className="mt-3 text-xs text-green-700">
              <p>Admission: {formatCurrency(summary.admission_fees)}</p>
              <p>Subscriptions: {formatCurrency(summary.subscription_fees)}</p>
              <p>Other: {formatCurrency(summary.other_income)}</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-red-700 font-medium">Total Expenses</p>
              <TrendingDown className="text-red-600" size={24} />
            </div>
            <p className="text-3xl font-black text-red-900">{formatCurrency(summary.total_expenses)}</p>
            <div className="mt-3 text-xs text-red-700">
              <p>Trainer Payments: {formatCurrency(summary.trainer_payments)}</p>
              <p>Other Expenses: {formatCurrency(summary.other_expenses)}</p>
            </div>
          </div>

          <div className={`bg-gradient-to-br ${
            summary.net_profit >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-orange-50 to-orange-100 border-orange-200'
          } border rounded-xl p-6`}>
            <div className="flex items-center justify-between mb-2">
              <p className={`text-sm font-medium ${summary.net_profit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                Net Profit/Loss
              </p>
              <DollarSign className={summary.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'} size={24} />
            </div>
            <p className={`text-3xl font-black ${summary.net_profit >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>
              {formatCurrency(summary.net_profit)}
            </p>
            <div className="mt-3 text-xs text-zinc-600">
              <p>Pending Admission: {formatCurrency(summary.pending_admission_fees)}</p>
              <p>Pending Subscriptions: {formatCurrency(summary.pending_subscription_fees)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <Label className="text-xs mb-1 block">Date Range</Label>
            <Select value={datePreset} onValueChange={handleDatePresetChange}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
                <SelectItem value="last_year">Last Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Start Date</Label>
            <Input
              type="date"
              value={dateFilter.start}
              onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
              className="h-9"
              disabled={datePreset !== 'custom'}
            />
          </div>
          <div>
            <Label className="text-xs mb-1 block">End Date</Label>
            <Input
              type="date"
              value={dateFilter.end}
              onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
              className="h-9"
              disabled={datePreset !== 'custom'}
            />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Category</Label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Admission Fee">Admission Fee</SelectItem>
                <SelectItem value="Subscription Fee">Subscription Fee</SelectItem>
                <SelectItem value="Other Income">Other Income</SelectItem>
                <SelectItem value="Trainer Payment">Trainer Payment</SelectItem>
                <SelectItem value="Rent">Rent</SelectItem>
                <SelectItem value="Utilities">Utilities</SelectItem>
                <SelectItem value="Equipment">Equipment</SelectItem>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
                <SelectItem value="Salary">Salary</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Payment Method</Label>
            <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All Methods" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Search Field - Full Width */}
        <div className="w-full">
          <Label className="text-xs mb-1 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
            <Input
              placeholder="Search by member name, description, or reference number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-9"
            />
          </div>
        </div>
      </div>

      {/* Ledger Table - Desktop */}
      <div className="hidden md:block bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
        <div className="p-4 border-b border-zinc-200 bg-zinc-50">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Transaction History</h2>
        </div>
        <table className="w-full">
          <thead className="bg-zinc-50 text-zinc-500 font-medium text-xs uppercase tracking-wider">
            <tr>
              <th className="px-6 py-4 text-left">Date</th>
              <th className="px-6 py-4 text-left">Type</th>
              <th className="px-6 py-4 text-left">Category</th>
              <th className="px-6 py-4 text-left">Description</th>
              <th className="px-6 py-4 text-left">Payment Method</th>
              <th className="px-6 py-4 text-left">Member</th>
              <th className="px-6 py-4 text-right">Amount</th>
              <th className="px-6 py-4 text-left">Reference</th>
              <th className="px-6 py-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {ledger.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-zinc-500">
                  No transactions yet. Start by adding receipts or expenses.
                </td>
              </tr>
            ) : (
              ledger.map((entry) => (
                <tr
                  key={entry.id}
                  className="hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0"
                >
                  <td className="px-6 py-4 text-sm">{formatDate(entry.date)}</td>
                  <td className="px-6 py-4">
                    {entry.type === 'receipt' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                        <Receipt size={12} />
                        Income
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                        <FileText size={12} />
                        Expense
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">{entry.category}</td>
                  <td className="px-6 py-4 text-sm text-zinc-600">{entry.description}</td>
                  <td className="px-6 py-4 text-sm capitalize">{entry.payment_method.replace('_', ' ')}</td>
                  <td className="px-6 py-4 text-sm">{entry.member_name || '-'}</td>
                  <td className={`px-6 py-4 text-right text-sm font-bold ${
                    entry.amount >= 0 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {entry.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(entry.amount))}
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-500">{entry.reference_number}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditEntry(entry)}
                        className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                      >
                        <Edit2 size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteEntry(entry)}
                        className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Transaction Cards - Mobile */}
      <div className="md:hidden">
        <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-4">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>Transaction History</h2>
        </div>
        <div className="space-y-3">
          {ledger.length === 0 ? (
            <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center text-zinc-500">
              No transactions yet. Start by adding receipts or expenses.
            </div>
          ) : (
            ledger.map((entry) => (
              <div
                key={entry.id}
                className="bg-white border border-zinc-200 rounded-lg p-4 shadow-sm"
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="text-xs text-zinc-500 mb-1">{formatDate(entry.date)}</div>
                    {entry.type === 'receipt' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                        <Receipt size={12} />
                        Income
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                        <FileText size={12} />
                        Expense
                      </span>
                    )}
                  </div>
                  <div className={`text-lg font-bold ${
                    entry.amount >= 0 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {entry.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(entry.amount))}
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Category:</span>
                    <span className="text-zinc-900 font-medium">{entry.category}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Payment:</span>
                    <span className="text-zinc-900 capitalize">{entry.payment_method.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Member:</span>
                    <span className="text-zinc-900">{entry.member_name || '-'}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-zinc-500">Description: </span>
                    <span className="text-zinc-900">{entry.description}</span>
                  </div>
                  {entry.reference_number && (
                    <div className="text-xs text-zinc-400 mt-2 pt-2 border-t border-zinc-100">
                      Ref: {entry.reference_number}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-100">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditEntry(entry)}
                    className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    <Edit2 size={14} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteEntry(entry)}
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 size={14} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Entry Dialog */}
      {editingEntry && (
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent aria-describedby="edit-dialog-description">
            <DialogHeader>
              <DialogTitle>
                Edit {editingEntry.type === 'receipt' ? 'Receipt' : 'Expense'}
              </DialogTitle>
            </DialogHeader>
            <p id="edit-dialog-description" className="text-sm text-zinc-600 mb-4">
              Ref: {editingEntry.reference_number}
            </p>
            <form onSubmit={handleUpdateEntry} className="space-y-4">
              <div>
                <Label>Category</Label>
                <Input
                  value={editingEntry.category}
                  disabled
                  className="bg-zinc-50"
                />
              </div>

              <div>
                <Label htmlFor="edit_date">Date</Label>
                <Input
                  id="edit_date"
                  type="date"
                  value={editingEntry.date}
                  onChange={(e) => setEditingEntry({ ...editingEntry, date: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_amount">Amount *</Label>
                  <Input
                    id="edit_amount"
                    type="number"
                    step="0.01"
                    value={editingEntry.amount}
                    onChange={(e) => setEditingEntry({ ...editingEntry, amount: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_payment_method">Payment Method *</Label>
                  <Select
                    value={editingEntry.payment_method}
                    onValueChange={(value) => setEditingEntry({ ...editingEntry, payment_method: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="edit_description">Description</Label>
                <Input
                  id="edit_description"
                  value={editingEntry.description || ""}
                  onChange={(e) => setEditingEntry({ ...editingEntry, description: e.target.value })}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-blue-500 hover:bg-blue-600">
                  Update
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Print Options Dialog */}
      {showPrintOptions && lastCreatedReceipt && (
        <Dialog open={showPrintOptions} onOpenChange={setShowPrintOptions}>
          <DialogContent aria-describedby="print-dialog-description">
            <DialogHeader>
              <DialogTitle>Receipt Created Successfully!</DialogTitle>
            </DialogHeader>
            <p id="print-dialog-description" className="text-sm text-zinc-600 mb-4">
              Receipt #{lastCreatedReceipt.receipt_number} - {formatCurrency(lastCreatedReceipt.amount)}
            </p>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => handlePrint('a4')}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Printer size={16} className="mr-2" />
                  Print A4
                </Button>
                <Button
                  onClick={() => handlePrint('a5')}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Printer size={16} className="mr-2" />
                  Print A5
                </Button>
              </div>
              
              <Button
                onClick={() => handlePrint('thermal')}
                className="w-full bg-purple-500 hover:bg-purple-600 text-white"
              >
                <Printer size={16} className="mr-2" />
                Print Thermal (58mm/80mm)
              </Button>
              
              <div className="border-t pt-3 mt-3">
                <p className="text-xs text-zinc-500 mb-2">Download or Share as PDF:</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleDownloadPDF('a4')}
                    variant="outline"
                    className="text-sm"
                  >
                    <Download size={14} className="mr-2" />
                    Download A4
                  </Button>
                  <Button
                    onClick={() => handleSharePDF('a4')}
                    variant="outline"
                    className="text-sm"
                  >
                    <Share2 size={14} className="mr-2" />
                    Share PDF
                  </Button>
                </div>
              </div>
              
              <Button
                onClick={() => setShowPrintOptions(false)}
                variant="ghost"
                className="w-full mt-2"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Hidden Receipt Component for Printing */}
      {lastCreatedReceipt && (
        <ReceiptPrint
          ref={receiptRef}
          receipt={lastCreatedReceipt}
          receiptType={lastCreatedReceipt.receipt_type}
        />
      )}
    </div>
  );
};

export default FinancialLedger;
